import Axios from 'axios';
import { API } from 'core';
import { AppConfig } from 'shared/app.config';
import { getOnCallAPIParams, getParamsForAPI } from '../helpers';
import {
  APTAMetricsByService,
  IncidentAnalyticsCountByAssignee,
  IncidentAnalyticsCountByPriority,
  IncidentAnalyticsCountByService,
  IncidentAnalyticsCountByServiceGroupByPriority,
  IncidentAnalyticsCountByStatus,
  IncidentAnalyticsCountByTeam,
  IncidentAnalyticsCountVsTime,
  IncidentAnalyticsCountVsTimeByPriority,
  IncidentAnalyticsCountVsTimeByStatus,
  IncidentAnalyticsFilterParams,
  IncidentAnalyticsFilterResponse,
  IncidentAnalyticsTotalCount,
  IncidentAnalyticsXTTA,
  IncidentAnalyticsXTTAByPriority,
  IncidentAnalyticsXTTAByService,
  IncidentAnalyticsXTTAVsTime,
  IncidentAnalyticsXTTR,
  IncidentAnalyticsXTTRByPriority,
  IncidentAnalyticsXTTRByService,
  IncidentAnalyticsXTTRVsTime,
  NoiseReductionByService,
  NoiseReductionByTeam,
  OnCallAnalyticsFilterResponse,
  OnCallUsersPerHour,
  OnCallUsersPerHourExportParams,
  OnCallUsersPerHourParams,
  TotalSilencedCount,
} from './types';

const createFileName = (params: IncidentAnalyticsFilterParams, endpoint: string) => {
  const paramsForAPI = getParamsForAPI(params);
  return `${endpoint}_${paramsForAPI.startDate}_${paramsForAPI.endDate}.csv`;
};

const downloadFile = (params: IncidentAnalyticsFilterParams, endpoint: string, response: any) => {
  const fileName = createFileName(params, endpoint);
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const getIncidentAnalyticsFilter = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsFilterResponse>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/filters?teamID=${params.teamID}`,
  );
  return response.data;
};

export const getOnCallAnalyticFilter = async (teamID: string) => {
  const { data: response } = await Axios.get<OnCallAnalyticsFilterResponse>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/schedules/oncall-hours-per-user/filters?owner_id=${teamID}`,
  );
  return response.data;
};

export const getIncidentAnalyticsTotalCount = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsTotalCount>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountByTeam = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsCountByTeam>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-team`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountByTeamExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-team/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_count_by_team', response);
};

export const getIncidentAnalyticsCountByStatusExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-status/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_count_by_status', response);
};

export const getIncidentAnalyticsCountByStatus = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsCountByStatus>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-status`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountByPriorityExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-priority/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_count_by_priority', response);
};

export const getIncidentAnalyticsCountByPriority = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const { data: response } = await Axios.get<IncidentAnalyticsCountByPriority>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-priority`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountByAssigneeExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-assignee/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_count_by_assignee', response);
};
export const getIncidentAnalyticsCountByAssignee = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const { data: response } = await Axios.get<IncidentAnalyticsCountByAssignee>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-assignee`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountByServiceGroupByPriorityExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-service-priority/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_count_by_service_priority', response);
};

export const getIncidentAnalyticsCountByServiceGroupByPriority = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const { data: response } = await Axios.get<IncidentAnalyticsCountByServiceGroupByPriority>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-service-priority`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountByServiceExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-service/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_count_by_service', response);
};

export const getIncidentAnalyticsCountByService = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsCountByService>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-by-service`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountVsTimeByStatusExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-vs-time-by-status/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_count_vs_time_by_status', response);
};

export const getIncidentAnalyticsCountVsTimeByStatus = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const { data: response } = await Axios.get<IncidentAnalyticsCountVsTimeByStatus>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-vs-time-by-status`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountVsTimeByPriorityExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-vs-time-by-priority/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_count_vs_time_by_priority', response);
};

export const getIncidentAnalyticsCountVsTimeByPriority = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const { data: response } = await Axios.get<IncidentAnalyticsCountVsTimeByPriority>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-vs-time-by-priority`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountVsTime = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsCountVsTime>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-vs-time`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsCountVsTimeExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const paramsForAPI = getParamsForAPI(params);
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/count-vs-time/export`,
    {
      params: paramsForAPI,
      withCredentials: true,
    },
  );

  // file name from content disposition header
  console.log({ response: response.headers });

  downloadFile(params, 'incident_count_vs_time', response);
};

export const getIncidentAnalyticsXTTA = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsXTTA>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xtta`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsXTTR = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsXTTR>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xttr`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsXTTAVsTime = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsXTTAVsTime>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xtta-vs-time`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsXTTAVsTimeExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xtta-vs-time/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_xtta_vs_time', response);
};

export const getIncidentAnalyticsXTTRVsTime = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsXTTRVsTime>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xttr-vs-time`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsXTTRVsTimeExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xttr-vs-time/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_xttr_vs_time', response);
};

export const getIncidentAnalyticsXTTAByPriorityExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xtta-by-priority/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_xtta_by_priority', response);
};

export const getIncidentAnalyticsXTTAByPriority = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsXTTAByPriority>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xtta-by-priority`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsXTTRByPriorityExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xttr-by-priority/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_xttr_by_priority', response);
};

export const getIncidentAnalyticsXTTRByPriority = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsXTTRByPriority>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xttr-by-priority`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsXTTRByServiceExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xttr-by-service/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_xttr_by_service', response);
};

export const getIncidentAnalyticsXTTRByService = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsXTTRByService>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xttr-by-service`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getIncidentAnalyticsXTTAByServiceExport = async (
  params: IncidentAnalyticsFilterParams,
) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xtta-by-service/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_xtta_by_service', response);
};

export const getIncidentAnalyticsXTTAByService = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<IncidentAnalyticsXTTAByService>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/xtta-by-service`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getTotalSilencedCount = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<TotalSilencedCount>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/silenced-count`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response.data;
};

export const getNoiseReductionByServiceExport = async (params: IncidentAnalyticsFilterParams) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/noise-reduction-by-service/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_noise_reduction_by_service', response);
};

export const getNoiseReductionByService = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<NoiseReductionByService>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/noise-reduction-by-service`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response;
};

export const getAPTAMetricsByServiceExport = async (params: IncidentAnalyticsFilterParams) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/apta-metrics/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_apta_metrics', response);
};
export const getAPTAMetricsByService = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<APTAMetricsByService>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/apta-metrics`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response;
};

export const getNoiseReductionByTeam = async (params: IncidentAnalyticsFilterParams) => {
  const { data: response } = await Axios.get<NoiseReductionByTeam>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/noise-reduction-by-team`,
    {
      params: getParamsForAPI(params),
    },
  );
  return response;
};

export const getNoiseReductionByTeamExport = async (params: IncidentAnalyticsFilterParams) => {
  const response = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/incidents/noise-reduction-by-team/export`,
    {
      params: getParamsForAPI(params),
    },
  );
  downloadFile(params, 'incident_noise_reduction_by_team', response);
};

export const getOnCallHoursPerUser = async (params: OnCallUsersPerHourParams, ownerId: string) => {
  const { data: response } = await Axios.get<OnCallUsersPerHour>(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/schedules/oncall-hours-per-user`,
    {
      params: getOnCallAPIParams(params),
    },
  );
  return response;
};

export const getOnCallHoursPerUserExport = async (
  params: OnCallUsersPerHourExportParams,
  ownerId: string,
) => {
  const { data: response } = await Axios.get(
    `${AppConfig.api_url}/v3/organizations/${API.config.organizationId}/analytics-v3/schedules/oncall-hours-per-user/export?owner_id=${ownerId}`,
    {
      params: params,
    },
  );
  return response;
};
